import { makeAutoObservable, runInAction } from 'mobx'
import { type StripeCardElementChangeEvent, type TokenResult } from '@stripe/stripe-js'
import { type IResponseInviteClientCard } from 'entities/Clients/api/types'

export class CheckoutCardStore {
  name = ''
  nameError = ''
  cardError = ''
  apiError = false
  private _cardReady = false
  private _onSubmitCard: (() => Promise<TokenResult | undefined>) | null = null

  constructor() {
    makeAutoObservable(this)
  }

  setName = (value: string) => {
    this.name = value
  }

  onChangeCard = (event: StripeCardElementChangeEvent) => {
    this._cardReady = event.complete
    this.cardError = event.error?.message || ''
  }

  setOnSubmit = (onSubmit: () => Promise<TokenResult | undefined>) => {
    this._onSubmitCard = onSubmit
  }

  clearCardError = () => {
    this.cardError = ''
  }

  clearApiError = () => {
    this.apiError = false
  }

  clearAllErrors = () => {
    this.nameError = ''
    this.clearCardError()
    this.clearApiError()
  }

  setApiError = (value: boolean) => {
    this.apiError = value
  }

  setCardError = (message: string) => {
    this.cardError = message
  }

  private _validate(): boolean {
    this.cardError = ''
    if (!this.name.trim().length) {
      this.nameError = 'Cardholder name is required'
    }
    if (!this._cardReady) {
      this.cardError = 'Credit card is required'
    }
    return !!this.nameError || !!this.cardError
  }

  getToken = async () => {
    this.clearAllErrors()
    if (this._validate()) return null
    if (!this._onSubmitCard) return null

    const res = await this._onSubmitCard()
    if (!res || res.error) {
      runInAction(() => {
        this.cardError = res?.error?.message || 'Please provide another credit card'
      })
      return null
    }
    return {
      tokenId: res.token.id,
      name: this.name.trim(),
      card: (res.token as { card?: IResponseInviteClientCard }).card ?? '',
    }
  }

  reset = () => {
    this.name = ''
    this.clearAllErrors()
    this._cardReady = false
    this._onSubmitCard = null
  }
}
